import { configureStore, combineReducers } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'
import { persistStore, persistReducer, createMigrate } from 'redux-persist'
import { createTransform } from 'redux-persist'
import type { PersistedState } from 'redux-persist/es/types'
import storage from 'redux-persist/lib/storage' // defaults to localStorage for web
import { atcSlice } from './atc/atcSlice'
import type { AtcState } from './atc/atcSlice'
import { userReducer } from './userSlice'
import { listenerMiddleware } from './middlewares/listener'
import { userPreferencesSlice } from './userPreferences/userPreferencesSlice'
import { appSlice } from './appState/appSlice'
import musicPlaylistReducer from './musicPlaylist/musicPlaylistSlice'
import { getAtcManifestService } from '@/app/services/atc/AtcManifestService'
import { DEFAULT_ATC_RADIO_EFFECT_STRENGTH } from '@/types/AtcRadioEffect'

type PersistedUserPreferencesState = Record<string, unknown> & {
  atcRadioEffectStrength?: unknown;
}

type PersistedRootState = (Exclude<PersistedState, undefined> & {
  userPreferences?: PersistedUserPreferencesState;
}) | undefined;

const stripAtcSelectionTransform = createTransform(
  (inboundState: AtcState) => {
    const selectedStation = inboundState?.selectedStation;
    if (!selectedStation) {
      return inboundState;
    }

    return {
      ...inboundState,
      selectedStation: {
        ...selectedStation,
        // Never persist scenario lists; always reload station manifest on each app load.
        scenarios: null,
      },
    };
  },
  (outboundState: AtcState) => {
    const selectedStation = outboundState?.selectedStation;
    if (!selectedStation) {
      return outboundState;
    }

    return {
      ...outboundState,
      selectedStation: {
        ...selectedStation,
        scenarios: null,
      },
    };
  }
);

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  migrate: createMigrate({
    1: (state: PersistedState) => {
      const persistedState = state as PersistedRootState;

      if (!persistedState || typeof persistedState !== 'object') {
        return persistedState;
      }

      const userPreferences = persistedState.userPreferences;

      if (!userPreferences || typeof userPreferences !== 'object') {
        return persistedState;
      }

      return {
        ...persistedState,
        userPreferences: {
          ...userPreferences,
          atcRadioEffectStrength:
            userPreferences.atcRadioEffectStrength ?? DEFAULT_ATC_RADIO_EFFECT_STRENGTH,
        },
      } satisfies Exclude<PersistedRootState, undefined>;
    },
  }),
  // Temporarily blacklist musicPlaylist to load fresh mock data
  // TODO: Remove this blacklist once real R2 data is implemented
  // ATC playback state must NOT be persisted; only station selection + per-scenario progress is persisted.
  blacklist: ['musicPlaylist', 'atc'],
}

const atcPersistConfig = {
  key: 'atc',
  storage,
  whitelist: ['selectedStation'],
  transforms: [stripAtcSelectionTransform],
}

const rootReducer = combineReducers({
  atc: persistReducer(atcPersistConfig, atcSlice.reducer),
  user: userReducer,
  userPreferences: userPreferencesSlice.reducer,
  app: appSlice.reducer,
  musicPlaylist: musicPlaylistReducer,
})

const persistedReducer = persistReducer(persistConfig, rootReducer)

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: {
        extraArgument: {
          atcManifestService: getAtcManifestService(),
        },
      },
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
      },
    }).prepend(listenerMiddleware.middleware),
  devTools: true
})

export const persistor = persistStore(store)

// Infer the `RootState` and `AppDispatch` types from the store itself
export type AppState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export type AppStore = typeof store

export type RootState = ReturnType<typeof store.getState>

export const useAppDispatch: () => AppDispatch = useDispatch